import React, { useState, useEffect } from 'react';
import Sidebar from './components/Sidebar';
import TopBar from './components/TopBar';
import TicketQueue from './components/TicketQueue';
import Simulator from './components/Simulator';
import Login from './components/Login';
import LandingPage from './components/LandingPage';
import KnowledgeBase from './components/KnowledgeBase';
import Performance from './components/Performance';
import ProfileView from './components/ProfileView';
import AdminPanel from './components/AdminPanel';
import AcademyInfoPages from './components/AcademyInfoPages';
import LegalPages from './components/LegalPages';
import Playbook from './components/Playbook';
import { initialTickets } from './data/mockTickets';
import { supabase } from './supabase/supabase';
import { AnimatePresence, motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
const { FiCheckCircle, FiAlertCircle } = FiIcons;
import SafeIcon from './common/SafeIcon';

function App() {
  const [user, setUser] = useState(null);
  const [showLanding, setShowLanding] = useState(true);
  const [showAdmin, setShowAdmin] = useState(false);
  const [publicPage, setPublicPage] = useState(null);
  const [activeView, setActiveView] = useState('queue');
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [tickets, setTickets] = useState(initialTickets);
  const [selectedTicketId, setSelectedTicketId] = useState(null);
  const [xp, setXp] = useState(0);
  const [currentTime, setCurrentTime] = useState(Date.now());
  const [notification, setNotification] = useState(null);

  const [siteSettings, setSiteSettings] = useState({
    site_name: 'NetDesk Pro',
    logo_url: '',
    favicon_url: '',
    support_email: 'support@netdesk.pro',
    seo_title: 'NetDesk Pro | Enterprise Analyst Training',
    seo_description: 'The industry-standard operating system for training world-class support analysts.',
    seo_keywords: 'IT training, simulator, help desk, sysadmin, certification',
    og_image_url: '',
    twitter_handle: '@netdeskpro'
  });

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(Date.now()), 1000);
    fetchSiteSettings();
    return () => clearInterval(timer);
  }, []);

  const updateMetaTag = (selector, attribute, value) => {
    if (!value) return;
    let element = document.querySelector(selector);
    if (!element) {
      element = document.createElement('meta');
      if (selector.includes('property')) {
        element.setAttribute('property', selector.match(/property="([^"]+)"/)[1]);
      } else if (selector.includes('name')) {
        element.setAttribute('name', selector.match(/name="([^"]+)"/)[1]);
      }
      document.head.appendChild(element);
    }
    element.setAttribute(attribute, value);
  };

  const updateFavicon = (url) => {
    if (!url) return;
    let link = document.querySelector("link[rel~='icon']");
    if (!link) {
      link = document.createElement('link');
      link.rel = 'icon';
      document.head.appendChild(link);
    }
    link.href = url;
  };

  const fetchSiteSettings = async () => {
    try {
      const { data, error } = await supabase
        .from('site_settings_1777044382647')
        .select('*')
        .eq('id', 1)
        .single();
        
      if (error) throw error;
      if (data) {
        setSiteSettings(data);
        
        // Dynamic Header Injection
        document.title = data.seo_title || data.site_name || 'NetDesk Pro';
        updateFavicon(data.favicon_url);
        
        // SEO Meta
        updateMetaTag('meta[name="description"]', 'content', data.seo_description);
        updateMetaTag('meta[name="keywords"]', 'content', data.seo_keywords);
        
        // Open Graph
        updateMetaTag('meta[property="og:title"]', 'content', data.seo_title);
        updateMetaTag('meta[property="og:description"]', 'content', data.seo_description);
        updateMetaTag('meta[property="og:image"]', 'content', data.og_image_url);
        updateMetaTag('meta[property="og:site_name"]', 'content', data.site_name);
        
        // Twitter
        updateMetaTag('meta[name="twitter:card"]', 'content', 'summary_large_image');
        updateMetaTag('meta[name="twitter:site"]', 'content', data.twitter_handle);
      }
    } catch (err) {
      console.error('Error fetching site settings:', err);
    }
  };

  const notify = (message, type = 'success') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 4000);
  };

  const handleResolveTicket = (id, success) => {
    const updated = tickets.map(t => t.id === id ? { ...t, status: 'resolved' } : t);
    setTickets(updated);
    
    if (success) {
      const ticket = tickets.find(t => t.id === id);
      const gain = ticket.tier === 2 ? 300 : 150;
      setXp(prev => prev + gain);
      notify(`Ticket Resolved: +${gain} XP`);
    } else {
      notify('Simulation Failed', 'error');
    }
    setSelectedTicketId(null);
  };

  const handleResetQueue = (tier) => {
    const now = Date.now();
    const updated = tickets.map(t => t.tier === tier ? { ...t, openedAt: now, status: 'open' } : t);
    setTickets(updated);
    notify(`Tier ${tier} Queue Reset: SLAs Restored`);
  };

  const stats = {
    resolved: tickets.filter(t => t.status === 'resolved').length,
    tier1Open: tickets.filter(t => t.tier === 1 && t.status === 'open').length,
    tier1Resolved: tickets.filter(t => t.tier === 1 && t.status === 'resolved').length,
    tier2Open: tickets.filter(t => t.tier === 2 && t.status === 'open').length,
    tier2Resolved: tickets.filter(t => t.tier === 2 && t.status === 'resolved').length
  };

  const tier2Unlocked = stats.tier1Resolved >= 50 || user?.tier2Access === true;

  if (showAdmin) {
    return (
      <AdminPanel 
        tickets={tickets} 
        onUpdateTickets={setTickets} 
        onBack={() => { setShowAdmin(false); fetchSiteSettings(); }} 
      />
    );
  }

  if (publicPage) {
    if (publicPage.type === 'academy') {
      return <AcademyInfoPages defaultTab={publicPage.tab} onBack={() => setPublicPage(null)} />
    }
    if (publicPage.type === 'legal') {
      return <LegalPages defaultTab={publicPage.tab} onBack={() => setPublicPage(null)} supportEmail={siteSettings.support_email} />
    }
  }

  if (showLanding && !user) {
    return (
      <LandingPage 
        onGetStarted={() => setShowLanding(false)} 
        onLogin={() => setShowLanding(false)}
        onShowAcademy={(tab) => setPublicPage({ type: 'academy', tab })}
        onShowLegal={(tab) => setPublicPage({ type: 'legal', tab })}
        siteName={siteSettings.site_name}
        supportEmail={siteSettings.support_email}
      />
    );
  }

  if (!user) {
    return (
      <Login 
        onLogin={setUser} 
        onAdminAccess={() => setShowAdmin(true)} 
        onBack={() => setShowLanding(true)}
      />
    );
  }

  return (
    <div className="flex h-screen bg-slate-100 font-sans overflow-hidden">
      <AnimatePresence>
        {notification && (
          <motion.div
            initial={{ opacity: 0, y: -50, x: '-50%' }}
            animate={{ opacity: 1, y: 24, x: '-50%' }}
            exit={{ opacity: 0, y: -50, x: '-50%' }}
            className="fixed left-1/2 z-[200] w-max max-w-[90vw]"
          >
            <div className={`px-6 py-3 rounded-2xl shadow-2xl border flex items-center space-x-3 backdrop-blur-md ${notification.type === 'error' ? 'bg-red-600 text-white border-red-500' : 'bg-slate-900/95 text-white border-slate-700'}`}>
              <SafeIcon icon={notification.type === 'error' ? FiAlertCircle : FiCheckCircle} />
              <span className="text-xs font-black uppercase tracking-widest">{notification.message}</span>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <Sidebar 
        activeView={activeView} 
        setActiveView={setActiveView} 
        stats={stats} 
        user={user}
        onLogout={() => setUser(null)}
        tier2Unlocked={tier2Unlocked}
        isOpen={isSidebarOpen}
        onClose={() => setIsSidebarOpen(false)}
        onSaveProgress={() => notify('Progress Synced to Cloud')}
        siteName={siteSettings.site_name}
      />
      
      <div className="flex-1 flex flex-col overflow-hidden relative">
        <TopBar 
          xp={xp} 
          tickets={tickets} 
          currentTime={currentTime} 
          onMenuOpen={() => setIsSidebarOpen(true)}
          user={user}
        />
        
        <main className="flex-1 overflow-y-auto relative bg-slate-50">
          {selectedTicketId ? (
            <div className="absolute inset-0 bg-white z-20">
              <Simulator 
                ticket={tickets.find(t => t.id === selectedTicketId)} 
                onBack={() => setSelectedTicketId(null)}
                onResolve={handleResolveTicket}
              />
            </div>
          ) : (
            <AnimatePresence mode="wait">
              <motion.div
                key={activeView}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className="h-full"
              >
                {activeView === 'queue' && (
                  <TicketQueue 
                    tickets={tickets.filter(t => t.tier === 1)} 
                    view="queue"
                    onSelectTicket={(t) => setSelectedTicketId(t.id)} 
                    onResetQueue={() => handleResetQueue(1)}
                    currentTime={currentTime}
                  />
                )}
                {activeView === 'tier2' && (
                  <TicketQueue 
                    tickets={tickets.filter(t => t.tier === 2)} 
                    view="Tier 2"
                    onSelectTicket={(t) => setSelectedTicketId(t.id)} 
                    onResetQueue={() => handleResetQueue(2)}
                    currentTime={currentTime}
                  />
                )}
                {activeView === 'resolved' && (
                  <TicketQueue 
                    tickets={tickets} 
                    view="resolved"
                    onSelectTicket={() => {}} 
                    currentTime={currentTime}
                  />
                )}
                {activeView === 'kb' && <KnowledgeBase />}
                {activeView === 'playbook' && (
                  <Playbook user={user} onLogout={() => setUser(null)} />
                )}
                {activeView === 'performance' && (
                  <Performance 
                    stats={stats} 
                    xp={xp} 
                    onClaimCertificate={() => notify('Certificate Claimed')} 
                    onViewSample={() => {}} 
                    tier2Unlocked={tier2Unlocked}
                  />
                )}
                {activeView === 'profile' && (
                  <ProfileView 
                    user={user} 
                    onSave={setUser} 
                    onNotify={notify} 
                  />
                )}
              </motion.div>
            </AnimatePresence>
          )}
        </main>
      </div>
    </div>
  );
}

export default App;